import { SxProp, View, Pressable, styled } from 'dripsy';
import React from 'react';
import { Icon, SVGIconList } from '../icons/Icon';
import { TextCaption } from '@/design-system/components/typography';
import { UniButton } from '../buttons';
import { ButtonVariant } from '../buttons/useButton.hook';

const PressableView = styled(Pressable)();

export enum AlertType {
  Warning = 'warning',
  Error = 'error',
  Info = 'info',
  Success = 'success'
}

export interface UniAlertProps {
  type: AlertType;
  message: string;
  title?: string;
  sx?: SxProp;
  allowClose?: boolean;
  suffixButton?: {
    label: string;
    onPress: () => void;
    size?: 'small' | 'medium' | 'large' | 'default';
    sx?: SxProp;
  };
}

const COLOR: Record<AlertType, SxProp> = {
  warning: {
    backgroundColor: '$alertWarning'
  },
  error: {
    backgroundColor: '$alertErrorBackground'
  },
  success: {
    backgroundColor: '$success'
  },
  info: {
    backgroundColor: '$primary'
  }
};

const TEXT_COLOR: Record<AlertType, string> = {
  warning: '$textAlertContrast',
  error: '$textAlertDefault',
  success: '$textAlertDefault',
  info: '$textAlertDefault'
};

export const UniAlert: React.FC<UniAlertProps> = ({ type, title, message, sx, allowClose, suffixButton, children }) => {
  const [show, setShow] = React.useState(true);

  if (!show) return null;

  const iconName: SVGIconList = type === 'error' ? 'alertDanger' : 'exclamation';
  const style = type === 'error' ? styles.wrapperError : styles.wrapper;
  const leftStyle = type === 'error' ? styles.leftError : styles.left;

  return (
    <View sx={{ ...style, ...COLOR[type], ...sx }}>
      <View sx={leftStyle}>
        <Icon name={iconName} height={20} width={20} color={'$alertErrorTitle'} themeColor={'$alertErrorTitle'} />
        <View sx={{ width: '$2' }} />
        <View sx={{ flex: 1 }}>
          {title && (
            <TextCaption bold label sx={{ color: TEXT_COLOR[type] }}>
              {title}
            </TextCaption>
          )}
          <TextCaption sx={{ ...styles.message, color: TEXT_COLOR[type] }}>{message}</TextCaption>
        </View>
      </View>
      <View sx={styles.right}>
        {allowClose && <CloseButton onPress={() => setShow(false)} />}
        {suffixButton && (
          <UniButton
            size={suffixButton.size || 'default'}
            label={suffixButton.label}
            onPress={suffixButton.onPress}
            sx={{ ...styles.suffixButton, ...suffixButton.onPress }}
            variant={ButtonVariant.outline1}
          />
        )}
      </View>
    </View>
  );
};

const CloseButton = ({ onPress }: { onPress: () => void }) => {
  return (
    <PressableView onPress={onPress}>
      <Icon name="cross" height={16} width={16} />
    </PressableView>
  );
};

enum StyleName {
  wrapper = 'wrapper',
  wrapperError = 'wrapperError',
  message = 'message',
  left = 'left',
  leftError = 'leftError',
  right = 'right',
  suffixButton = 'suffixButton'
}

const styles: Record<StyleName, SxProp> = {
  wrapper: {
    paddingX: '$3',
    paddingY: '$2',
    borderRadius: 5,
    backgroundColor: '$warning',
    flexDirection: 'row',
    alignItems: 'center'
  },
  wrapperError: {
    paddingX: '$4',
    paddingY: '$3',
    borderRadius: 5,
    backgroundColor: '$warning',
    flexDirection: 'row',
    alignItems: 'flex-start'
  },
  message: {
    flex: 1,
    maxWidth: '100%',
    wordBreak: 'break-word',
    color: '$textPrimary'
  },
  left: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center'
  },
  leftError: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center'
  },
  right: {},
  suffixButton: {
    width: 70,
    paddingY: '$3'
  }
};
